import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import React from 'react';
import { useAuth } from '../../context/authContext';
import { Building2, Mail, Shield, User } from 'lucide-react';

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const ProfileField = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-3.5 border-b border-slate-100 last:border-0">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
            <Icon className="h-4 w-4 text-slate-500" />
        </span>
        <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">{label}</p>
            <p className="text-sm font-medium text-[#2E2E2E] break-words">{value}</p>
        </div>
    </div>
);

const SettingsCard = () => {
    const { user } = useAuth();
    const roleVariant = user.role === 'Admin' ? 'info' : 'secondary';

    return (
        <Card className="overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-4">
                    {user.picture ? (
                        <img
                            src={user.picture}
                            alt={user.name}
                            className="h-14 w-14 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                    ) : (
                        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#2E2E2E] text-lg font-semibold text-white">
                            {getInitials(user.name)}
                        </span>
                    )}
                    <div className="min-w-0 flex flex-col gap-1">
                        <CardTitle className="text-lg truncate leading-tight mb-0">{user.name}</CardTitle>
                        <CardDescription className="truncate mt-0 leading-snug">{user.email}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-2">
                <ProfileField icon={Building2} label="Organisation" value={user.organisationName} />
                <ProfileField icon={Mail} label="Email" value={user.email} />
                <div className="flex items-start gap-3 py-3.5 border-b border-slate-100">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                        <User className="h-4 w-4 text-slate-500" />
                    </span>
                    <div className="flex-1">
                        <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">Role</p>
                        <div className="mt-1.5">
                            <Badge variant={roleVariant}>{user.role}</Badge>
                        </div>
                    </div>
                </div>
                <div className="flex items-start gap-3 py-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                        <Shield className="h-4 w-4 text-slate-500" />
                    </span>
                    <div className="flex-1">
                        <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">Security</p>
                        <div className="mt-1.5">
                            <Badge variant={user.twoFactorEnabled ? 'success' : 'warning'}>
                                {user.twoFactorEnabled ? '2FA enabled' : '2FA not configured'}
                            </Badge>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default SettingsCard;
